import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { TranslatePipe } from '@ngx-translate/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { PostTradingExpiredOrdersComponent } from '@shared/components/trading/orders/orders-options/tables/post-trading/post-trading-expired-orders/post-trading-expired-orders.component'
import { OptionsOpenedOrdersComponent } from '@shared/components/trading/orders/orders-options/tables/options/options-opened-orders/options-opened-orders.component'
import { OptionsCanceledOrdersComponent } from '@shared/components/trading/orders/orders-options/tables/options/options-canceled-orders/options-canceled-orders.component'
import { PostTradingFilterComponent } from '@shared/components/trading/orders/orders-options/tables/post-trading/post-trading-filter/post-trading-filter.component'
import { TuiDayRange } from '@taiga-ui/cdk'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { WidgetsService } from '@modules/widgets-module/services/widgets.service'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { OptionOrderTypeEnum } from '@modules/options-module/orders/enums/option-order-type.enum'
import { PostTradingEventTypeEnum } from '@modules/options-module/post-trading/enums/post-trading-event-type.enum'
import { SelectInterface } from '@shared/interfaces/select.interface'
import { OptionsOrdersTypesConstant } from '@modules/options-module/orders/constants/options-orders-types'
import { OptionsExecutedOrdersComponent } from './tables/options/options-executed-orders/options-executed-orders.component'
import { PostTradingExercisesOrdersComponent } from './tables/post-trading/post-trading-exercises-orders/post-trading-exercises-orders.component'
import { PostTradingAssignedOrdersComponent } from './tables/post-trading/post-trading-assigned-orders/post-trading-assigned-orders.component'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { SelectComponent } from '@shared/components/ui-kit/select/select.component'
import { AssetTypesComponent } from '@shared/components/common/asset-type/asset-type.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-orders-options',
	templateUrl: './orders-options.component.html',
	styleUrl: './orders-options.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TranslatePipe,
		OptionsExecutedOrdersComponent,
		PostTradingExpiredOrdersComponent,
		PostTradingExercisesOrdersComponent,
		OptionsOpenedOrdersComponent,
		OptionsCanceledOrdersComponent,
		PostTradingAssignedOrdersComponent,
		PostTradingFilterComponent,
		ButtonComponent,
		SelectComponent,
		AssetTypesComponent,
	],
})
export class OrdersOptionsComponent {
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum
	protected readonly optionOrderTypeEnum: typeof OptionOrderTypeEnum = OptionOrderTypeEnum
	protected readonly postTradingEventTypeEnum: typeof PostTradingEventTypeEnum = PostTradingEventTypeEnum
	protected readonly optionsOrdersTypesConstant: SelectInterface<OptionOrderTypeEnum>[] = OptionsOrdersTypesConstant

	protected readonly orderOptionsTables: OptionOrderStateEnum[] = [
		this.optionOrderStateEnum.OPEN,
		this.optionOrderStateEnum.CANCELED,
		this.optionOrderStateEnum.FILLED,
	]
	protected readonly orderPostTradingTables: PostTradingEventTypeEnum[] = [
		this.postTradingEventTypeEnum.EXPIRED,
		this.postTradingEventTypeEnum.EXERCISES,
		this.postTradingEventTypeEnum.ASSIGNED,
	]

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)

	private readonly _widgetsService: WidgetsService = inject<WidgetsService>(WidgetsService)

	protected postTradingSearchTicker: WritableSignal<string> = signal<string>('')
	protected postTradingDatesRange: WritableSignal<TuiDayRange | null> = signal<TuiDayRange | null>(null)

	protected selectedOrderType: Signal<SelectInterface<OptionOrderTypeEnum> | undefined> = computed(
		(): SelectInterface<OptionOrderTypeEnum> | undefined => {
			const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
			if (!widget) return

			return (
				this.optionsOrdersTypesConstant.find(
					(type: SelectInterface<OptionOrderTypeEnum>): boolean =>
						type.value === widget.settings.options.activeType,
				) ?? this.defaultOrderType
			)
		},
	)

	protected activeOrderType: Signal<OptionOrderStateEnum | PostTradingEventTypeEnum> = computed(
		(): OptionOrderStateEnum | PostTradingEventTypeEnum => {
			const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
			if (!widget) return this.optionOrderStateEnum.OPEN

			const { options }: OrdersSettingsInterface = widget.settings

			return options.activeType === this.optionOrderTypeEnum.ORDERS
				? options.orders.activeState
				: options.postTrading.activeType
		},
	)

	protected isPostTradingActive: Signal<boolean> = computed((): boolean => {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return false

		return widget.settings.options.activeType === this.optionOrderTypeEnum.POST_TRADING
	})

	get defaultOrderType(): SelectInterface<OptionOrderTypeEnum> | undefined {
		return (
			this.optionsOrdersTypesConstant.find((type: SelectInterface<OptionOrderTypeEnum>): boolean => {
				return type.value === this.optionOrderTypeEnum.ORDERS
			}) ?? this.optionsOrdersTypesConstant[0]
		)
	}
	protected onPostTradingDatesRange(range: TuiDayRange | null): void {
		this.postTradingDatesRange.set(range)
	}

	protected onPostTradingSearchTicker(ticker: string): void {
		this.postTradingSearchTicker.set(ticker)
	}

	protected changeOrderType(type: SelectInterface<OptionOrderTypeEnum>): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				options: {
					...widget.settings.options,
					activeType: type.value,
				},
			},
		})
	}

	protected setOrderType(order: OptionOrderStateEnum | PostTradingEventTypeEnum): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		const { options }: OrdersSettingsInterface = widget.settings
		const isOrders: boolean = options.activeType === this.optionOrderTypeEnum.ORDERS
		const isPostTrading: boolean = options.activeType === this.optionOrderTypeEnum.POST_TRADING

		this._widgetsService.updateWidget({
			...widget,
			settings: {
				...widget.settings,
				options: {
					...options,
					orders: isOrders ? { ...options.orders, activeState: order } : options.orders,
					postTrading: isPostTrading ? { ...options.postTrading, activeType: order } : options.postTrading,
				},
			},
		})
	}
}
